import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

export const WORKFORCE_PAYROLL_CONTRACT_VERSION = "WORKFORCE_PAYROLL_V1" as const;

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const IdempotencyKeySchema = Type.String({
  minLength: 8,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$",
});
const MonthSchema = Type.String({ pattern: "^[0-9]{4}-(?:0[1-9]|1[0-2])$" });
const TimestampSchema = Type.String({ minLength: 24, maxLength: 24 });
const Sha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const MoneySchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const VersionSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const NoteSchema = Type.String({ minLength: 2, maxLength: 300 });
const NameSchema = Type.String({ minLength: 1, maxLength: 80 });
const PAYSLIP_NUMBER_PATTERN = /^GZ-(\d{4})(\d{2})-([0-9A-F]{12})$/u;
const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;

export const WorkforcePayrollRoleSchema = Type.Union([
  Type.Literal("MANAGER"),
  Type.Literal("CASHIER"),
  Type.Literal("WAITER"),
  Type.Literal("COACH"),
]);
export type WorkforcePayrollRole = Static<typeof WorkforcePayrollRoleSchema>;

export const WorkforcePayrollPayModeSchema = Type.Union([
  Type.Literal("MONTHLY"),
  Type.Literal("HOURLY"),
]);
export type WorkforcePayrollPayMode = Static<typeof WorkforcePayrollPayModeSchema>;

export const WorkforcePayrollStateSchema = Type.Union([
  Type.Literal("PENDING_MANAGER_REVIEW"),
  Type.Literal("NEEDS_OWNER_REVISION"),
  Type.Literal("PENDING_EMPLOYEE_CONFIRMATION"),
  Type.Literal("DISPUTED"),
  Type.Literal("READY_TO_LOCK"),
  Type.Literal("LOCKED"),
  Type.Literal("PAID"),
]);
export type WorkforcePayrollState = Static<typeof WorkforcePayrollStateSchema>;

export const WorkforcePayrollManualLineSchema = Type.Object(
  {
    lineId: StableIdSchema,
    category: Type.Union([
      Type.Literal("ALLOWANCE"),
      Type.Literal("DEDUCTION"),
      Type.Literal("PRIOR_PERIOD_ADJUSTMENT"),
    ]),
    direction: Type.Union([Type.Literal("EARNING"), Type.Literal("DEDUCTION")]),
    label: Type.String({ minLength: 1, maxLength: 80 }),
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    reason: NoteSchema,
    sourcePayslipNumber: Type.Union([
      Type.String({ pattern: "^GZ-[0-9]{6}-[0-9A-F]{12}$" }),
      Type.Null(),
    ]),
  },
  { additionalProperties: false },
);
export type WorkforcePayrollManualLine = Static<typeof WorkforcePayrollManualLineSchema>;

export const WorkforcePayrollAttendanceSourceSchema = Type.Object(
  {
    scheduleCount: MoneySchema,
    completedAttendanceCount: MoneySchema,
    leaveCount: MoneySchema,
    absentCount: MoneySchema,
    plannedMinutes: MoneySchema,
    workedMinutes: MoneySchema,
    overtimeMinutes: MoneySchema,
    sourceSha256: Sha256Schema,
  },
  { additionalProperties: false },
);
export type WorkforcePayrollAttendanceSource = Static<
  typeof WorkforcePayrollAttendanceSourceSchema
>;

export const WorkforcePayrollCommissionSourceSchema = Type.Object(
  {
    coverage: Type.Literal("COACH_SERVICE_ONLY"),
    entryCount: MoneySchema,
    netCommissionFen: Type.Integer({
      minimum: -Number.MAX_SAFE_INTEGER,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    sourceSha256: Sha256Schema,
  },
  { additionalProperties: false },
);
export type WorkforcePayrollCommissionSource = Static<
  typeof WorkforcePayrollCommissionSourceSchema
>;

export const WorkforcePayrollMoneyLineSchema = Type.Object(
  {
    lineId: StableIdSchema,
    kind: Type.Union([
      Type.Literal("BASE_PAY"),
      Type.Literal("COACH_COMMISSION"),
      Type.Literal("ALLOWANCE"),
      Type.Literal("DEDUCTION"),
      Type.Literal("PRIOR_PERIOD_ADJUSTMENT"),
    ]),
    direction: Type.Union([Type.Literal("EARNING"), Type.Literal("DEDUCTION")]),
    label: Type.String({ minLength: 1, maxLength: 80 }),
    amountFen: MoneySchema,
    explanation: Type.String({ minLength: 2, maxLength: 300 }),
    sourcePayslipNumber: Type.Union([
      Type.String({ pattern: "^GZ-[0-9]{6}-[0-9A-F]{12}$" }),
      Type.Null(),
    ]),
  },
  { additionalProperties: false },
);
export type WorkforcePayrollMoneyLine = Static<typeof WorkforcePayrollMoneyLineSchema>;

export const WorkforcePayrollTimelineEntrySchema = Type.Object(
  {
    version: VersionSchema,
    operation: Type.Union([
      Type.Literal("CREATED"),
      Type.Literal("REVISED"),
      Type.Literal("MANAGER_APPROVED"),
      Type.Literal("MANAGER_REJECTED"),
      Type.Literal("EMPLOYEE_CONFIRMED"),
      Type.Literal("EMPLOYEE_DISPUTED"),
      Type.Literal("LOCKED"),
      Type.Literal("PAID"),
    ]),
    state: WorkforcePayrollStateSchema,
    actorId: StableIdSchema,
    actorDisplayName: NameSchema,
    note: NoteSchema,
    occurredAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type WorkforcePayrollTimelineEntry = Static<typeof WorkforcePayrollTimelineEntrySchema>;

export const WorkforcePayrollPayslipSchema = Type.Object(
  {
    contractVersion: Type.Literal(WORKFORCE_PAYROLL_CONTRACT_VERSION),
    payslipId: StableIdSchema,
    payslipNumber: Type.String({ pattern: "^GZ-[0-9]{6}-[0-9A-F]{12}$" }),
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    month: MonthSchema,
    employeeId: StableIdSchema,
    employeeNumber: Type.String({ minLength: 1, maxLength: 80 }),
    employeeName: NameSchema,
    employeeRoles: Type.Array(WorkforcePayrollRoleSchema, {
      minItems: 1,
      maxItems: 4,
      uniqueItems: true,
    }),
    employmentVersion: VersionSchema,
    payMode: WorkforcePayrollPayModeSchema,
    baseRateFen: MoneySchema,
    baseAmountFen: MoneySchema,
    attendance: WorkforcePayrollAttendanceSourceSchema,
    commission: WorkforcePayrollCommissionSourceSchema,
    manualLines: Type.Array(WorkforcePayrollManualLineSchema, { maxItems: 20 }),
    moneyLines: Type.Array(WorkforcePayrollMoneyLineSchema, { minItems: 1, maxItems: 22 }),
    earningsFen: MoneySchema,
    deductionsFen: MoneySchema,
    payableFen: MoneySchema,
    state: WorkforcePayrollStateSchema,
    version: VersionSchema,
    timeline: Type.Array(WorkforcePayrollTimelineEntrySchema, { minItems: 1, maxItems: 100 }),
    lockedAt: Type.Union([TimestampSchema, Type.Null()]),
    paidAt: Type.Union([TimestampSchema, Type.Null()]),
    paymentReference: Type.Union([Type.String({ minLength: 2, maxLength: 120 }), Type.Null()]),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
    evidence: Type.Literal("GATEWAY_AUTHORITY"),
  },
  { additionalProperties: false },
);
export type WorkforcePayrollPayslip = Static<typeof WorkforcePayrollPayslipSchema>;

const PayrollDraftFields = {
  employeeId: StableIdSchema,
  month: MonthSchema,
  expectedEmploymentVersion: VersionSchema,
  payMode: WorkforcePayrollPayModeSchema,
  baseRateFen: MoneySchema,
  manualLines: Type.Array(WorkforcePayrollManualLineSchema, { maxItems: 20 }),
  note: NoteSchema,
  idempotencyKey: IdempotencyKeySchema,
};

export const CreateWorkforcePayrollRequestSchema = Type.Object(PayrollDraftFields, {
  additionalProperties: false,
});
export type CreateWorkforcePayrollRequest = Static<typeof CreateWorkforcePayrollRequestSchema>;

export const ReviseWorkforcePayrollRequestSchema = Type.Object(
  { ...PayrollDraftFields, expectedPayslipVersion: VersionSchema },
  { additionalProperties: false },
);
export type ReviseWorkforcePayrollRequest = Static<typeof ReviseWorkforcePayrollRequestSchema>;

export const ReviewWorkforcePayrollRequestSchema = Type.Object(
  {
    decision: Type.Union([Type.Literal("APPROVE"), Type.Literal("REJECT")]),
    expectedPayslipVersion: VersionSchema,
    note: NoteSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ReviewWorkforcePayrollRequest = Static<typeof ReviewWorkforcePayrollRequestSchema>;

export const RespondWorkforcePayrollRequestSchema = Type.Object(
  {
    decision: Type.Union([Type.Literal("CONFIRM"), Type.Literal("DISPUTE")]),
    expectedPayslipVersion: VersionSchema,
    note: NoteSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RespondWorkforcePayrollRequest = Static<typeof RespondWorkforcePayrollRequestSchema>;

export const LockWorkforcePayrollRequestSchema = Type.Object(
  {
    expectedPayslipVersion: VersionSchema,
    note: NoteSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type LockWorkforcePayrollRequest = Static<typeof LockWorkforcePayrollRequestSchema>;

export const RecordWorkforcePayrollPaymentRequestSchema = Type.Object(
  {
    expectedPayslipVersion: VersionSchema,
    paidAmountFen: MoneySchema,
    paymentReference: Type.String({ minLength: 2, maxLength: 120 }),
    note: NoteSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RecordWorkforcePayrollPaymentRequest = Static<
  typeof RecordWorkforcePayrollPaymentRequestSchema
>;

export const WorkforcePayrollOperationCommandSchema = Type.Union([
  Type.Object(
    {
      operationType: Type.Literal("CREATE"),
      payslipId: Type.Null(),
      request: CreateWorkforcePayrollRequestSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      operationType: Type.Literal("REVISE"),
      payslipId: StableIdSchema,
      request: ReviseWorkforcePayrollRequestSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      operationType: Type.Literal("REVIEW"),
      payslipId: StableIdSchema,
      request: ReviewWorkforcePayrollRequestSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      operationType: Type.Literal("RESPOND"),
      payslipId: StableIdSchema,
      request: RespondWorkforcePayrollRequestSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      operationType: Type.Literal("LOCK"),
      payslipId: StableIdSchema,
      request: LockWorkforcePayrollRequestSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      operationType: Type.Literal("RECORD_PAYMENT"),
      payslipId: StableIdSchema,
      request: RecordWorkforcePayrollPaymentRequestSchema,
    },
    { additionalProperties: false },
  ),
]);
export type WorkforcePayrollOperationCommand = Static<
  typeof WorkforcePayrollOperationCommandSchema
>;

export const WorkforcePayrollOperationResultSchema = Type.Object(
  {
    command: WorkforcePayrollOperationCommandSchema,
    payslip: WorkforcePayrollPayslipSchema,
  },
  { additionalProperties: false },
);
export type WorkforcePayrollOperationResult = Static<typeof WorkforcePayrollOperationResultSchema>;

export const WorkforcePayrollWorkspaceSchema = Type.Object(
  {
    contractVersion: Type.Literal(WORKFORCE_PAYROLL_CONTRACT_VERSION),
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    viewer: Type.Union([Type.Literal("SELF"), Type.Literal("MANAGEMENT")]),
    viewerEmployeeId: Type.Union([StableIdSchema, Type.Null()]),
    month: MonthSchema,
    payslips: Type.Array(WorkforcePayrollPayslipSchema, { maxItems: 500 }),
    generatedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type WorkforcePayrollWorkspace = Static<typeof WorkforcePayrollWorkspaceSchema>;

export const WorkforcePayrollBusinessEventPayloadSchema = Type.Object(
  { payslip: WorkforcePayrollPayslipSchema, operation: WorkforcePayrollOperationCommandSchema },
  { additionalProperties: false },
);
export type WorkforcePayrollBusinessEventPayload = Static<
  typeof WorkforcePayrollBusinessEventPayloadSchema
>;

function isCanonicalTimestamp(value: string | null): boolean {
  if (value === null) return true;
  const epoch = Date.parse(value);
  return Number.isFinite(epoch) && new Date(epoch).toISOString() === value;
}

function safeSum(values: readonly number[]): number | null {
  let total = 0;
  for (const value of values) {
    total += value;
    if (!Number.isSafeInteger(total)) return null;
  }
  return total;
}

function validManualLine(line: WorkforcePayrollManualLine): boolean {
  if (line.category === "ALLOWANCE" && line.direction !== "EARNING") return false;
  if (line.category === "DEDUCTION" && line.direction !== "DEDUCTION") return false;
  return (line.category === "PRIOR_PERIOD_ADJUSTMENT") === (line.sourcePayslipNumber !== null);
}

export function isWorkforcePayrollPayslip(value: unknown): value is WorkforcePayrollPayslip {
  if (!Value.Check(WorkforcePayrollPayslipSchema, value)) return false;
  const payslip = value as WorkforcePayrollPayslip;
  if (
    !PAYSLIP_NUMBER_PATTERN.test(payslip.payslipNumber) ||
    !isCanonicalTimestamp(payslip.createdAt) ||
    !isCanonicalTimestamp(payslip.updatedAt) ||
    !isCanonicalTimestamp(payslip.lockedAt) ||
    !isCanonicalTimestamp(payslip.paidAt) ||
    new Set(payslip.employeeRoles).size !== payslip.employeeRoles.length ||
    new Set(payslip.manualLines.map((line) => line.lineId)).size !== payslip.manualLines.length ||
    new Set(payslip.moneyLines.map((line) => line.lineId)).size !== payslip.moneyLines.length ||
    !payslip.manualLines.every(validManualLine) ||
    payslip.timeline.length !== payslip.version ||
    payslip.timeline.some((entry, index) => {
      const previous = index === 0 ? null : payslip.timeline[index - 1];
      return (
        entry.version !== index + 1 ||
        !isCanonicalTimestamp(entry.occurredAt) ||
        (previous !== null && previous !== undefined && entry.occurredAt < previous.occurredAt)
      );
    }) ||
    payslip.timeline.at(-1)?.state !== payslip.state ||
    payslip.timeline.at(-1)?.version !== payslip.version
  ) {
    return false;
  }
  const earnings = safeSum(
    payslip.moneyLines.filter((line) => line.direction === "EARNING").map((line) => line.amountFen),
  );
  const deductions = safeSum(
    payslip.moneyLines
      .filter((line) => line.direction === "DEDUCTION")
      .map((line) => line.amountFen),
  );
  if (
    earnings === null ||
    deductions === null ||
    payslip.earningsFen !== earnings ||
    payslip.deductionsFen !== deductions ||
    deductions > earnings ||
    payslip.payableFen !== earnings - deductions ||
    payslip.moneyLines.filter((line) => line.kind === "BASE_PAY").length !== 1 ||
    payslip.moneyLines.filter((line) => line.kind === "COACH_COMMISSION").length > 1
  ) {
    return false;
  }
  const locked = payslip.state === "LOCKED" || payslip.state === "PAID";
  const paid = payslip.state === "PAID";
  if (
    locked !== (payslip.lockedAt !== null) ||
    paid !== (payslip.paidAt !== null && payslip.paymentReference !== null)
  ) {
    return false;
  }
  return (
    !paid ||
    (payslip.paidAt !== null && payslip.lockedAt !== null && payslip.paidAt >= payslip.lockedAt)
  );
}

export function isWorkforcePayrollWorkspace(value: unknown): value is WorkforcePayrollWorkspace {
  if (!Value.Check(WorkforcePayrollWorkspaceSchema, value)) return false;
  const workspace = value as WorkforcePayrollWorkspace;
  if (!isCanonicalTimestamp(workspace.generatedAt)) return false;
  const ids = new Set<string>();
  for (const payslip of workspace.payslips) {
    if (
      !isWorkforcePayrollPayslip(payslip) ||
      payslip.tenantId !== workspace.tenantId ||
      payslip.storeId !== workspace.storeId ||
      payslip.month !== workspace.month ||
      (workspace.viewer === "SELF" && payslip.employeeId !== workspace.viewerEmployeeId) ||
      ids.has(payslip.payslipId)
    ) {
      return false;
    }
    ids.add(payslip.payslipId);
  }
  return workspace.viewer === "SELF"
    ? workspace.viewerEmployeeId !== null
    : workspace.viewerEmployeeId === null;
}

export function isWorkforcePayrollOperationResult(
  value: unknown,
): value is WorkforcePayrollOperationResult {
  if (!Value.Check(WorkforcePayrollOperationResultSchema, value)) return false;
  const result = value as WorkforcePayrollOperationResult;
  return (
    isWorkforcePayrollPayslip(result.payslip) &&
    (result.command.payslipId === null || result.command.payslipId === result.payslip.payslipId) &&
    result.command.request.idempotencyKey.length >= 8
  );
}

export function isWorkforcePayrollOperationCommand(
  value: unknown,
): value is WorkforcePayrollOperationCommand {
  return Value.Check(WorkforcePayrollOperationCommandSchema, value);
}

function hashSuffix(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function deriveWorkforcePayrollNumber(month: string, sourceId: string): string {
  if (!/^\d{4}-(?:0[1-9]|1[0-2])$/u.test(month) || sourceId.length < 12) {
    throw new TypeError("payroll number source is invalid");
  }
  return `GZ-${month.replace("-", "")}-${hashSuffix(sourceId)}`;
}
